import { FC, ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	ColorModeValue,
	DesktopComputerOutline,
	HStack,
	IconButton,
	MoonOutline,
	rem,
	SunOutline,
	Text,
	Tooltip,
	useCustomTheme,
	VStack,
} from '@bonusly/design-system';

import { Reveal } from '../../../components/display/reveal';
import { useUserColorModeWithSync } from '../../../hooks/use-user-color-mode-with-sync';

type ThemeMenuItemProps = {
	isExpanded: boolean;
};

// Color configuration for each mode when selected
const modeColors: Record<ColorModeValue, { bg: string; color: string; hoverBg: string }> = {
	system: { bg: 'blue.100', color: 'blue.600', hoverBg: 'blue.200' },
	light: { bg: 'yellow.100', color: 'yellow.200', hoverBg: 'yellow.25' },
	dark: { bg: 'purple.100', color: 'purple.600', hoverBg: 'purple.200' },
};

export const ThemeMenuItem: FC<ThemeMenuItemProps> = ({ isExpanded }) => {
	const { t } = useTranslation('navigation');
	const { userPreference, setColorMode, canChangeColorMode } = useUserColorModeWithSync();
	const { activePreset, clearTheme } = useCustomTheme();

	// Handle color mode change, clearing full themes when switching away from dark mode
	// Full themes only define dark mode colors, so switching to light/system mode
	// would result in a broken visual mix. Clear the theme first to ensure clean transition.
	const handleColorModeChange = useCallback(
		(mode: ColorModeValue) => {
			if (mode !== 'dark' && activePreset?.category === 'full') {
				clearTheme();
			}
			setColorMode(mode);
		},
		[activePreset, clearTheme, setColorMode]
	);

	if (!canChangeColorMode) {
		return null;
	}

	const modes: { value: ColorModeValue; icon: ReactElement; label: string }[] = [
		{ value: 'system', icon: <DesktopComputerOutline size="sm" />, label: t('user_menu.theme_system') },
		{ value: 'light', icon: <SunOutline size="sm" />, label: t('user_menu.theme_light') },
		{ value: 'dark', icon: <MoonOutline size="sm" />, label: t('user_menu.theme_dark') },
	];

	return (
		<VStack w="100%" spacing={0} align="stretch">
			<Box w="100%" py={rem(8)} px={rem(10)}>
				<HStack justifyContent="space-between" alignItems="center" spacing={3}>
					<HStack justifyContent={isExpanded ? 'flex-start' : 'center'} alignItems={isExpanded ? 'flex-start' : 'center'} spacing={3}>
						<Reveal if={isExpanded}>
							<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap" color="text.default">
								{t('user_menu.theme')}
							</Text>
						</Reveal>
					</HStack>
					<HStack spacing={rem(4)} bg="bg.subtle" borderRadius="md" p={1}>
						{modes.map((mode) => {
							const isSelected = userPreference === mode.value;
							const colors = modeColors[mode.value];

							return (
								<Tooltip key={mode.value} label={mode.label} placement="top">
									<IconButton
										aria-label={mode.label}
										icon={mode.icon}
										size="xs"
										variant="ghost"
										borderRadius="sm"
										bg={isSelected ? 'bg.surface' : 'transparent'}
										color={isSelected ? colors.color : 'text.soft'}
										//boxShadow={isSelected ? 'sm' : 'none'}
										_hover={{
											bg: isSelected ? colors.hoverBg : 'secondary.bgAlphaHover',
										}}
										onClick={(e) => {
											e.stopPropagation();
											handleColorModeChange(mode.value);
										}}
									/>
								</Tooltip>
							);
						})}
					</HStack>
				</HStack>
			</Box>
		</VStack>
	);
};
